import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import TabBar from '../components/TabBar';
import { API_BASE } from '../services/apiBase';

const API    = API_BASE;
const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

function authHeaders() {
  return { Authorization: 'Bearer ' + localStorage.getItem('access_token') };
}

const DEFAULT_RULES = {
  artist_separation:   { enabled: true,  mode: 'hard', min_tracks: 4,  penalty: 80 },
  artist_repeat_hours: { enabled: true,  mode: 'soft', hours: 2,        penalty: 60 },
  bpm_transition:      { enabled: false, mode: 'soft', max_change: 30,  penalty: 40 },
  bpm_range:           { enabled: false, mode: 'soft', min_bpm: 80, max_bpm: 140, penalty: 50 },
  mood_repeat:         { enabled: true,  mode: 'soft', max_consecutive: 2, penalty: 30 },
  energy_curve:        { enabled: false, mode: 'soft', penalty: 40 },
};

// ── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
        background: value ? '#22c55e' : '#30363d',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: value ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

// ── HARD / SOFT mode pill ─────────────────────────────────────────────────────
function ModePill({ value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #30363d', opacity: disabled ? 0.4 : 1 }}>
      {['hard', 'soft'].map(mode => {
        const active = value === mode;
        const bg = active ? (mode === 'hard' ? 'rgba(239,68,68,0.2)' : 'rgba(47,129,247,0.2)') : 'transparent';
        const color = active ? (mode === 'hard' ? '#ef4444' : '#58a6ff') : '#484f58';
        const border = active ? (mode === 'hard' ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(47,129,247,0.4)') : 'none';
        return (
          <button
            key={mode}
            disabled={disabled}
            onClick={() => !disabled && onChange(mode)}
            style={{
              padding: '5px 14px', border, background: bg, color,
              cursor: disabled ? 'default' : 'pointer',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
              transition: 'all 0.15s',
            }}
          >
            {mode.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

// ── Penalty slider ────────────────────────────────────────────────────────────
function PenaltySlider({ value, onChange, disabled }) {
  const pct = value;
  const color = pct >= 70 ? '#ef4444' : pct >= 40 ? '#f59e0b' : '#22c55e';
  return (
    <div style={{ opacity: disabled ? 0.4 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em' }}>PENALTY WEIGHT</span>
        <span style={{ fontSize: 13, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      </div>
      <div style={{ position: 'relative', height: 6, borderRadius: 3, background: '#21262d', overflow: 'visible' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.1s, background 0.2s' }} />
        <input
          type="range" min={0} max={100} value={value}
          disabled={disabled}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            position: 'absolute', top: '50%', left: 0, right: 0, width: '100%',
            transform: 'translateY(-50%)', opacity: 0, cursor: disabled ? 'default' : 'pointer',
            height: 20, margin: 0,
          }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 10, color: '#484f58' }}>0 — ignored</span>
        <span style={{ fontSize: 10, color: '#484f58' }}>100 — absolute</span>
      </div>
    </div>
  );
}

// ── Number input ──────────────────────────────────────────────────────────────
function NumInput({ label, value, onChange, min, max, disabled, unit }) {
  return (
    <div style={{ opacity: disabled ? 0.4 : 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em', marginBottom: 5 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="number" min={min} max={max} value={value}
          disabled={disabled}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            width: 72, background: '#0d1117', border: '1px solid #30363d', borderRadius: 7,
            padding: '6px 10px', color: '#e6edf3', fontSize: 14, fontWeight: 700,
            fontVariantNumeric: 'tabular-nums', outline: 'none',
          }}
        />
        {unit && <span style={{ fontSize: 12, color: '#8b949e' }}>{unit}</span>}
      </div>
    </div>
  );
}

// ── Rule card ─────────────────────────────────────────────────────────────────
function RuleCard({ title, description, ruleKey, rules, update }) {
  const rule = rules[ruleKey];
  if (!rule) return null;
  const disabled = !rule.enabled;

  return (
    <div style={{
      background: '#161b22', border: `1px solid ${rule.enabled ? '#30363d' : '#21262d'}`,
      borderRadius: 12, padding: 20, transition: 'border-color 0.2s',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ flex: 1, marginRight: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: rule.enabled ? '#e6edf3' : '#484f58', marginBottom: 3, transition: 'color 0.2s' }}>
            {title}
          </div>
          <div style={{ fontSize: 12, color: '#8b949e', lineHeight: '16px' }}>{description}</div>
        </div>
        <Toggle value={rule.enabled} onChange={v => update(ruleKey, 'enabled', v)} />
      </div>

      {/* Mode toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em', width: 40 }}>MODE</span>
        <ModePill value={rule.mode} onChange={v => update(ruleKey, 'mode', v)} disabled={disabled} />
        <span style={{ fontSize: 11, color: '#484f58' }}>
          {rule.mode === 'hard' ? 'Never violated — track skipped if no compliant candidate' : 'Preferred — violated with penalty if needed'}
        </span>
      </div>

      {/* Rule-specific controls */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
        {ruleKey === 'artist_separation' && (
          <NumInput label="MIN TRACKS BETWEEN" value={rule.min_tracks} min={1} max={20}
            onChange={v => update(ruleKey, 'min_tracks', v)} disabled={disabled} unit="tracks" />
        )}
        {ruleKey === 'artist_repeat_hours' && (
          <NumInput label="REPEAT WINDOW" value={rule.hours} min={1} max={24}
            onChange={v => update(ruleKey, 'hours', v)} disabled={disabled} unit="hours" />
        )}
        {ruleKey === 'bpm_transition' && (
          <NumInput label="MAX BPM JUMP" value={rule.max_change} min={5} max={100}
            onChange={v => update(ruleKey, 'max_change', v)} disabled={disabled} unit="BPM" />
        )}
        {ruleKey === 'bpm_range' && (
          <>
            <NumInput label="MIN BPM" value={rule.min_bpm} min={40} max={300}
              onChange={v => update(ruleKey, 'min_bpm', v)} disabled={disabled} unit="BPM" />
            <NumInput label="MAX BPM" value={rule.max_bpm} min={40} max={300}
              onChange={v => update(ruleKey, 'max_bpm', v)} disabled={disabled} unit="BPM" />
          </>
        )}
        {ruleKey === 'mood_repeat' && (
          <NumInput label="MAX CONSECUTIVE" value={rule.max_consecutive} min={1} max={10}
            onChange={v => update(ruleKey, 'max_consecutive', v)} disabled={disabled} unit="tracks" />
        )}
      </div>

      {/* Penalty slider */}
      <PenaltySlider value={rule.penalty} onChange={v => update(ruleKey, 'penalty', v)} disabled={disabled} />
    </div>
  );
}

// ── Tab button ────────────────────────────────────────────────────────────────
// ── Main ──────────────────────────────────────────────────────────────────────
export default function RulesEngine() {
  const [stores, setStores]   = useState([]);
  const [storeId, setStoreId] = useState('');
  const [rules, setRules]     = useState(DEFAULT_RULES);
  const [tab, setTab]         = useState('artist');
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [dirty, setDirty]     = useState(false);

  // Load stores
  useEffect(() => {
    axios.get(`${API}/tenants/${TENANT}/stores`, { headers: authHeaders() })
      .then(r => {
        const list = r.data?.items || r.data || [];
        setStores(list);
        if (list.length > 0) setStoreId(list[0].id);
      })
      .catch(console.error);
  }, []);

  // Load rules when store changes
  const loadRules = useCallback(async () => {
    try {
      const params = new URLSearchParams({ tenant_id: TENANT });
      if (storeId) params.append('store_id', storeId);
      const r = await axios.get(`${API}/rotations/rules?${params}`, { headers: authHeaders() });
      setRules({ ...DEFAULT_RULES, ...r.data });
      setDirty(false);
    } catch (e) {
      console.error(e);
      setRules(DEFAULT_RULES);
    }
  }, [storeId]);

  useEffect(() => { if (storeId) loadRules(); }, [loadRules, storeId]);

  const update = (ruleKey, field, value) => {
    setRules(prev => ({ ...prev, [ruleKey]: { ...prev[ruleKey], [field]: value } }));
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/rotations/rules`, {
        tenant_id: TENANT,
        store_id: storeId || null,
        rules,
      }, { headers: authHeaders() });
      setSaved(true);
      setDirty(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const reset = () => { setRules(DEFAULT_RULES); setDirty(true); setSaved(false); };

  const activeCount = (keys) => keys.filter(k => rules[k]?.enabled).length;

  const TABS = [
    { key: 'artist', label: 'Artist',  ruleKeys: ['artist_separation', 'artist_repeat_hours'] },
    { key: 'tempo',  label: 'Tempo',   ruleKeys: ['bpm_transition', 'bpm_range'] },
    { key: 'mood',   label: 'Mood',    ruleKeys: ['mood_repeat', 'energy_curve'] },
  ];

  const currentTab = TABS.find(t => t.key === tab);

  const RULE_META = {
    artist_separation:   { title: 'Artist Separation', description: 'Enforce a minimum number of tracks between plays by the same artist.' },
    artist_repeat_hours: { title: 'Artist Repeat Window', description: 'Prevent the same artist from appearing more than once within a set time window.' },
    bpm_transition:      { title: 'BPM Transition', description: 'Limit the tempo jump between consecutive tracks for smooth energy flow.' },
    bpm_range:           { title: 'BPM Range', description: 'Restrict tracks to a target BPM range during this rotation period.' },
    mood_repeat:         { title: 'Mood Repeat Limit', description: 'Cap how many consecutive tracks can share the same mood tag.' },
    energy_curve:        { title: 'Energy Curve', description: 'Encourage energy levels to follow a time-of-day arc (build in mornings, wind down in evenings).' },
  };

  const hardCount = Object.values(rules).filter(r => r.enabled && r.mode === 'hard').length;
  const softCount = Object.values(rules).filter(r => r.enabled && r.mode === 'soft').length;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#e6edf3' }}>Rules Engine</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8b949e' }}>
              Configure scheduling constraints for the rotation engine — HARD rules are never violated, SOFT rules carry a penalty weight
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {saved && <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>✓ Saved</span>}
            {dirty && !saved && <span style={{ fontSize: 12, color: '#f59e0b' }}>Unsaved changes</span>}
            <select
              value={storeId}
              onChange={e => setStoreId(e.target.value)}
              style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 7, padding: '7px 12px', color: '#e6edf3', fontSize: 13 }}
            >
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={reset} style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid #30363d', background: 'transparent', color: '#8b949e', cursor: 'pointer', fontSize: 13 }}>
              Reset defaults
            </button>
            <button
              onClick={save}
              disabled={saving || !dirty}
              style={{ padding: '7px 18px', borderRadius: 7, border: 'none', background: saving || !dirty ? '#21262d' : '#2f81f7', color: saving || !dirty ? '#484f58' : '#fff', cursor: saving || !dirty ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              {saving ? 'Saving…' : 'Save Rules'}
            </button>
          </div>
        </div>

        {/* Summary pills */}
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ padding: '4px 12px', borderRadius: 20, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', fontSize: 12, fontWeight: 700, color: '#ef4444' }}>
              {hardCount} HARD
            </span>
            <span style={{ padding: '4px 12px', borderRadius: 20, background: 'rgba(47,129,247,0.12)', border: '1px solid rgba(47,129,247,0.25)', fontSize: 12, fontWeight: 700, color: '#58a6ff' }}>
              {softCount} SOFT
            </span>
            <span style={{ padding: '4px 12px', borderRadius: 20, background: '#21262d', fontSize: 12, color: '#8b949e' }}>
              {Object.values(rules).filter(r => !r.enabled).length} disabled
            </span>
          </div>
        </div>
      </div>

      <TabBar
        tabs={TABS.map(t => ({ id: t.key, label: t.label, count: activeCount(t.ruleKeys) }))}
        active={tab}
        onChange={setTab}
      />

      {/* Rule cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {currentTab?.ruleKeys.map(key => (
          <RuleCard
            key={key}
            ruleKey={key}
            title={RULE_META[key].title}
            description={RULE_META[key].description}
            rules={rules}
            update={update}
          />
        ))}
      </div>

      {/* Footer note */}
      <div style={{ marginTop: 24, padding: '14px 18px', background: '#161b22', border: '1px solid #30363d', borderRadius: 10 }}>
        <div style={{ fontSize: 12, color: '#8b949e', lineHeight: '18px' }}>
          <strong style={{ color: '#e6edf3' }}>How rules are applied:</strong> HARD rules exclude non-compliant tracks from the candidate pool entirely. SOFT rules reduce a track's selection score by the penalty weight — higher penalty = less likely to be chosen when a better option exists. Rules are evaluated per clock slot at queue generation time.
        </div>
      </div>
    </div>
  );
}
